/**
 * Transcode profiles written by administrators in FFmpeg syntax: the output
 * options between PRISM's input and the output file. PRISM always supplies
 * the input and output itself, so a profile can choose codecs, filters and
 * container settings but cannot name other files, devices or URLs.
 */

const FORBIDDEN_OPTIONS = new Set([
  '-i', '-y', '-n', '-filter_script', '-filter_complex_script', '-attach', '-dump_attachment', '-passlogfile',
  '-progress', '-report', '-vstats', '-vstats_file', '-sdp_file', '-stats_period', '-init_hw_device', '-filter_hw_device',
]);

/** Split like a shell would (whitespace, single and double quotes), without expanding anything. */
export function splitArgs(text: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quote: '"' | "'" | null = null;
  let has = false;
  for (const ch of text.trim()) {
    if (quote) {
      if (ch === quote) quote = null;
      else cur += ch;
      continue;
    }
    if (ch === '"' || ch === "'") { quote = ch; has = true; continue; }
    if (/\s/.test(ch)) {
      if (has) out.push(cur);
      cur = '';
      has = false;
      continue;
    }
    cur += ch;
    has = true;
  }
  if (quote) throw new Error('A quote is not closed');
  if (has) out.push(cur);
  return out;
}

/**
 * The earlier check (before 1.0.0-rc.2): a denylist. Profiles saved before the allowlist keep using it until they
 * are edited (the job says `legacy`).
 */
export function parseLegacyProfileArgs(text: string): string[] {
  const args = splitArgs(text);
  if (!args.length) throw new Error('Write the FFmpeg output options, for example: -c:v libx264 -crf 20 -c:a aac');
  if (args.length > 200) throw new Error('Too many arguments (200 at most)');
  for (const a of args) {
    if (FORBIDDEN_OPTIONS.has(a)) throw new Error(`“${a}” is not allowed: PRISM supplies the input, output and progress reporting itself`);
    if (/:\/\//.test(a)) throw new Error(`“${a}” looks like a URL; profiles cannot read or write other locations`);
    if (/(^|[=:,;'"(\s])(\/|~|\.\.)/.test(a) || a.startsWith('\\')) throw new Error(`“${a}” names a file path; profiles cannot read or write other files`);
    if (/\b(movie|amovie)=/.test(a)) throw new Error(`“${a}” reads another file; profiles cannot use movie sources`);
  }
  return args;
}

/**
 * FFmpeg output options a profile may use (1.0.0-rc.2), by name without a stream specifier (-c:v:0 is -c).
 * Codec, rate control, picture, audio, mapping, metadata and container options; nothing that names a file,
 * device, URL or another input.
 */
export const PROFILE_OPTIONS = new Set([
  // codecs and rate control
  '-c', '-codec', '-vcodec', '-acodec', '-scodec', '-dcodec', '-b', '-ab', '-vb', '-minrate', '-maxrate', '-bufsize', '-rc_init_occupancy', '-crf', '-qp', '-q',
  '-qscale', '-qmin', '-qmax', '-qdiff', '-global_quality', '-preset', '-tune', '-profile', '-level', '-tier', '-vprofile', '-rc', '-cq', '-cbr', '-vbr',
  '-x264opts', '-x264-params', '-x265-params', '-svtav1-params', '-aom-params', '-avcintra-class', '-nal-hrd', '-forced-idr', '-a53cc', '-b_ref_mode',
  '-spatial_aq', '-temporal_aq', '-aq-mode', '-aq-strength', '-look_ahead', '-rc-lookahead', '-deadline', '-cpu-used', '-row-mt', '-tile-columns',
  '-tile-rows', '-lag-in-frames', '-auto-alt-ref', '-quality', '-speed', '-compression_level', '-frame_size', '-cutoff', '-aac_coder', '-strict',
  '-vendor', '-bits_per_mb', '-quant_mat', '-alpha_bits', '-mbd', '-trellis', '-dc', '-intra', '-intra_vlc', '-non_linear_quant', '-alternate_scan',
  '-b_strategy', '-coder', '-context', '-mpv_flags', '-lmin', '-lmax', '-i_qfactor', '-b_qfactor', '-ps', '-bf', '-refs', '-g', '-keyint_min',
  '-sc_threshold', '-threads', '-slices', '-weightp', '-me_method', '-subq', '-partitions', '-flags', '-flags2', '-fflags', '-hwaccel_output_format',
  '-pix_fmt', '-sample_fmt', '-color_primaries', '-color_trc', '-colorspace', '-color_range', '-chroma_sample_location', '-field_order', '-top',
  '-timecode', '-gop_timecode', '-drop_frame_timecode', '-video_format', '-bsf', '-tag', '-vtag', '-atag', '-disposition',
  // picture and timing
  '-r', '-s', '-aspect', '-vf', '-af', '-filter', '-filter_complex', '-lavfi', '-fps_mode', '-vsync', '-async', '-frames', '-vframes', '-aframes', '-t', '-to',
  '-ss', '-shortest', '-copyts', '-start_at_zero', '-avoid_negative_ts', '-max_muxing_queue_size', '-muxdelay', '-muxpreload', '-max_interleave_delta',
  // audio
  '-ar', '-ac', '-channel_layout', '-ch_layout', '-audio_service_type', '-apad',
  // streams and metadata
  '-map', '-map_metadata', '-map_chapters', '-metadata', '-an', '-vn', '-sn', '-dn', '-id3v2_version', '-write_id3v1', '-write_tmcd', '-write_xing',
  // containers
  '-f', '-movflags', '-brand', '-use_editlist', '-frag_duration', '-frag_size', '-min_frag_duration', '-store_user_comments', '-signal_standard',
  '-mxf_audio_edit_rate', '-cluster_size_limit', '-cluster_time_limit', '-dash', '-live', '-reserve_index_space', '-mpegts_flags', '-mpegts_service_id',
  '-mpegts_pmt_start_pid', '-mpegts_start_pid', '-mpegts_copyts', '-pcr_period', '-muxrate', '-packetsize',
]);
/** Options that take no value. */
const FLAG_OPTIONS = new Set(['-an', '-vn', '-sn', '-dn', '-shortest', '-copyts', '-start_at_zero', '-intra', '-dash', '-live', '-write_tmcd']);
/** Containers that write files of their own beside the output (playlists, segments, images): not allowed. */
const FORBIDDEN_FORMATS = new Set(['hls', 'dash', 'segment', 'stream_segment', 'ssegment', 'image2', 'tee', 'fifo', 'rtp', 'rtsp', 'rtmp', 'srt', 'udp', 'tcp', 'http', 'ffmetadata', 'null']);
/** Filters that read or write files, devices or the network, or run commands. */
const FORBIDDEN_FILTERS = new Set([
  'movie', 'amovie', 'subtitles', 'ass', 'sendcmd', 'asendcmd', 'zmq', 'azmq', 'lut3d', 'lut1d', 'frei0r', 'frei0r_src', 'ladspa', 'lv2', 'ocr',
  'dnn_processing', 'dnn_detect', 'dnn_classify', 'sr', 'derain', 'signature', 'vidstabdetect', 'vidstabtransform', 'vmaf', 'libvmaf',
  'openclsrc', 'program_opencl', 'coreimage', 'coreimagesrc',
]);
/** Option names inside filter arguments that name a file. */
const FILE_KEYS = /(^|[=:,'"\s])(file(name)?|textfile|fontfile|stats_file|log_path|model|result|filename_\w+|stats|f)\s*=/i;

function checkValue(option: string, value: string): void {
  if (/:\/\//.test(value)) throw new Error(`“${value}” looks like a URL; profiles cannot read or write other locations`);
  if (/(^|[=:,;'"(\s])(\/|~|\.\.)/.test(value) || value.startsWith('\\')) throw new Error(`“${value}” names a file path; profiles cannot read or write other files`);
  if (option === '-f' && FORBIDDEN_FORMATS.has(value.toLowerCase())) throw new Error(`The “${value}” format writes files of its own; choose a container such as mp4, mov or mxf`);
  if (['-vf', '-af', '-filter', '-filter_complex', '-lavfi'].includes(option)) {
    for (const name of value.split(/[;,]/).map((part) => part.replace(/\[[^\]]*\]/g, '').replace(/['"\\]/g, '').trim().split(/[=@]/)[0]!.trim().toLowerCase()).filter(Boolean)) {
      if (FORBIDDEN_FILTERS.has(name)) throw new Error(`The “${name}” filter is not allowed in a profile: it reads or writes files, or runs commands`);
    }
    if (FILE_KEYS.test(value)) throw new Error(`“${value}” names a file; profiles cannot read or write other files`);
  }
  if (option === '-map' && !/^-?\[?[0-9a-z_:?\]]+$/i.test(value)) throw new Error(`“${value}” is not a stream map PRISM accepts (for example 0:v:0)`);
}

/**
 * Parse and check a profile's arguments (1.0.0-rc.2): every option must be in PROFILE_OPTIONS, values may not name
 * files, URLs or devices, and filters that read or write files are refused. Throws with a readable reason.
 */
export function parseProfileArgs(text: string): string[] {
  const args = splitArgs(text);
  if (!args.length) throw new Error('Write the FFmpeg output options, for example: -c:v libx264 -crf 20 -c:a aac');
  if (args.length > 200) throw new Error('Too many arguments (200 at most)');
  for (let i = 0; i < args.length; i++) {
    const a = args[i]!;
    if (!a.startsWith('-') || /^-\d/.test(a)) throw new Error(`“${a}” is not an option; write options as -name value (for example -crf 20)`);
    const name = a.split(':')[0]!;
    if (FORBIDDEN_OPTIONS.has(name)) throw new Error(`“${a}” is not allowed: PRISM supplies the input, output and progress reporting itself`);
    if (!PROFILE_OPTIONS.has(name)) throw new Error(`“${a}” is not one of the FFmpeg options profiles may use (docs/operations.md, “Transcode profiles”, lists them)`);
    if (FLAG_OPTIONS.has(name)) continue;
    const value = args[i + 1];
    if (value === undefined) throw new Error(`“${a}” needs a value`);
    checkValue(name, value);
    i++;
  }
  return args;
}

/** Why a profile saved before the allowlist does not pass it (null: it does). */
export function profileReviewReason(text: string): string | null {
  try {
    parseProfileArgs(text);
    return null;
  } catch (err) {
    return (err as Error).message;
  }
}

/** FFmpeg's -protocol_whitelist for a profile's input: local files and pipes, and https for presigned object storage URLs. */
export const profileProtocolWhitelist = (input: string): string => (/^https?:\/\//.test(input) ? 'file,pipe,http,https,tcp,tls,crypto' : 'file,pipe');

const MIME: Record<string, string> = {
  mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', mxf: 'application/mxf', webm: 'video/webm', mkv: 'video/x-matroska',
  ts: 'video/mp2t', avi: 'video/x-msvideo', m4a: 'audio/mp4', aac: 'audio/aac', wav: 'audio/wav', mp3: 'audio/mpeg', flac: 'audio/flac', ogg: 'audio/ogg',
  // Original format exports (0.99.0) keep any original's extension.
  aif: 'audio/aiff', aiff: 'audio/aiff', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', tif: 'image/tiff', tiff: 'image/tiff', gif: 'image/gif',
  webp: 'image/webp', zip: 'application/zip', pdf: 'application/pdf',
};
export const mimeForExtension = (ext: string) => MIME[ext.toLowerCase()] ?? 'application/octet-stream';

/** Containers and video codecs browsers play, which a proxy profile must produce. */
export const PROXY_EXTENSIONS = ['mp4', 'webm'] as const;
export const BROWSER_VIDEO_CODECS = ['h264', 'vp9', 'vp8', 'av1'];
